import { isSkillTierAllowed, SKILL_TIERS, type SkillTier } from '@highlight/common'
import { usePlanFeatures } from '@renderer/chat/hooks/use-plan-features'
import { useUnifiedBilling } from '@renderer/chat/store/global-billing'
import { usePaywallState } from '@renderer/chat/store/paywalls'
import type { StagedAction } from '../../hooks/staged-actions/types'

/**
 * Planner v2 stages what Free's locked skills would do as a title-only preview,
 * naming the tier that unlocks it in `metadata.lockedTier`. The gateway refuses
 * to draft or send one until the plan reaches that tier.
 */
export function lockedTierOf(action: Pick<StagedAction, 'metadata'> | undefined): SkillTier | null {
  const tier = action?.metadata?.lockedTier
  return (SKILL_TIERS as ReadonlyArray<string>).includes(tier ?? '') ? (tier as SkillTier) : null
}

/**
 * Whether the action is a preview the user's plan still doesn't unlock. Until
 * the plan loads it stays locked: an unknown plan reads as every feature.
 */
export function useIsActionLocked(action: Pick<StagedAction, 'metadata'> | undefined): boolean {
  const features = usePlanFeatures()
  const { subscription } = useUnifiedBilling()
  const { isLoading } = usePaywallState()
  const tier = lockedTierOf(action)
  if (tier === null) return false
  return isLoading || !subscription.plan || !isSkillTierAllowed(features, tier)
}
